import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';

// Define types
export interface Team {
  id: string;
  name: string;
  users: string[];
  owner_profile_id: string;
  admins: string[];
  languages?: string[];
  created_at: string;
}

export interface Project {
  id: string;
  name: string;
  team_id: string;
  user_id: string;
  created_at: string;
  languages: string[];
  placeholder_format: string;
}

interface WorkspaceContextType {
  teams: Team[];
  projects: Project[];
  selectedTeam: Team | null;
  selectedProject: Project | null;
  setSelectedTeam: (team: Team | null) => void;
  setSelectedProject: (project: Project | null) => void;
  setTeams: (teams: Team[] | ((prev: Team[]) => Team[])) => void;
  createProject: (teamId: string, name: string, placeholderFormat: string) => Promise<Project>;
  updateProject: (projectId: string, name: string, placeholderFormat: string) => Promise<Project>;
  deleteProject: (projectId: string) => Promise<void>;
  createTeam: (name: string) => Promise<Team>;
  updateTeam: (teamId: string, data: Partial<Team>) => Promise<Team>;
  deleteTeam: (teamId: string) => Promise<void>;
  isLoading: boolean;
}

export const WorkspaceContext = createContext<WorkspaceContextType>({
  teams: [],
  projects: [],
  selectedTeam: null,
  selectedProject: null,
  setSelectedTeam: () => {},
  setSelectedProject: () => {},
  setTeams: () => {},
  createProject: async () => {
    throw new Error('Not implemented');
  },
  updateProject: async () => {
    throw new Error('Not implemented');
  },
  deleteProject: async () => {
    throw new Error('Not implemented');
  },
  createTeam: async () => {
    throw new Error('Not implemented');
  },
  updateTeam: async () => {
    throw new Error('Not implemented');
  },
  deleteTeam: async () => {
    throw new Error('Not implemented');
  },
  isLoading: false
});

export const useWorkspace = () => useContext(WorkspaceContext);

export const WorkspaceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [teams, setTeams] = useState<Team[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Load saved team and project IDs from localStorage
  useEffect(() => {
    if (user) {
      const savedTeamId = localStorage.getItem(`${user.id}_selectedTeamId`);
      const savedProjectId = localStorage.getItem(`${user.id}_selectedProjectId`);
      
      // We'll use these IDs when teams and projects are loaded
    }
  }, [user]);

  // Custom setters that update localStorage
  const handleSetSelectedTeam = (team: Team | null) => {
    setSelectedTeam(team);
    if (user && team) {
      localStorage.setItem(`${user.id}_selectedTeamId`, team.id);
    } else if (user) {
      localStorage.removeItem(`${user.id}_selectedTeamId`);
    }
  };

  const handleSetSelectedProject = (project: Project | null) => {
    setSelectedProject(project);
    if (user && project) {
      localStorage.setItem(`${user.id}_selectedProjectId`, project.id);
    } else if (user) {
      localStorage.removeItem(`${user.id}_selectedProjectId`);
    }
  };

  const createProject = async (teamId: string, name: string, placeholderFormat: string): Promise<Project> => {
    if (!user) throw new Error('User not authenticated');

    const { data: newProject, error } = await supabase
      .from('Projects')
      .insert([{
        name,
        team_id: teamId,
        languages: ['en'],
        placeholder_format: placeholderFormat
      }])
      .select()
      .single();

    if (error) throw error;
    if (!newProject) throw new Error('Failed to create project');

    // Update local state
    setProjects(prev => [...prev, newProject]);
    
    // Select the new project
    handleSetSelectedProject(newProject);
    
    return newProject;
  };

  const updateProject = async (projectId: string, name: string, placeholderFormat: string): Promise<Project> => {
    if (!user) throw new Error('User not authenticated');

    const { data: updatedProject, error } = await supabase
      .from('Projects')
      .update({ name, placeholder_format: placeholderFormat })
      .eq('id', projectId)
      .select()
      .single();

    if (error) throw error;
    if (!updatedProject) throw new Error('Failed to update project');

    // Update local state
    setProjects(prev => prev.map(p => 
      p.id === projectId ? updatedProject : p
    ));

    // Update selected project if it was edited
    if (selectedProject?.id === projectId) {
      handleSetSelectedProject(updatedProject);
    }

    return updatedProject;
  };

  const deleteProject = async (projectId: string): Promise<void> => {
    if (!user) throw new Error('User not authenticated');

    const { error } = await supabase
      .from('Projects')
      .delete()
      .eq('id', projectId);

    if (error) throw error;

    // Update local state
    setProjects(prev => prev.filter(p => p.id !== projectId));

    // If deleted project was selected, select another project from the same team
    if (selectedProject?.id === projectId && selectedTeam) {
      const teamProjects = projects.filter(p => p.team_id === selectedTeam.id && p.id !== projectId);
      handleSetSelectedProject(teamProjects[0] || null);
    }
  };

  const createTeam = async (name: string): Promise<Team> => {
    if (!user) throw new Error('User not authenticated');

    // Get the user's profile ID
    const { data: profile, error: profileError } = await supabase
      .from('Profiles')
      .select('id')
      .eq('user_id', user.id)
      .single();

    if (profileError) throw profileError;
    if (!profile) throw new Error('User profile not found');

    const { data: team, error: createError } = await supabase
      .from('Teams')
      .insert([
        { 
          name,
          owner_profile_id: profile.id,
          users: [user.id],
          tov_product_context: '',
          tov_tone: ''
        }
      ])
      .select()
      .single();

    if (createError) throw createError;
    if (!team) throw new Error('Failed to create team');

    // Update local state
    setTeams(prev => [...prev, team]);
    
    // Select the new team
    handleSetSelectedTeam(team);
    
    return team;
  };

  const updateTeam = async (teamId: string, data: Partial<Team>): Promise<Team> => {
    if (!user) throw new Error('User not authenticated');

    const { data: updatedTeam, error } = await supabase
      .from('Teams')
      .update(data)
      .eq('id', teamId)
      .select()
      .single();

    if (error) throw error;
    if (!updatedTeam) throw new Error('Failed to update team');

    // Update local state
    setTeams(prev => prev.map(t => 
      t.id === teamId ? updatedTeam : t
    ));

    // Update selected team if it was edited
    if (selectedTeam?.id === teamId) {
      handleSetSelectedTeam(updatedTeam);
    }

    return updatedTeam;
  };

  const deleteTeam = async (teamId: string): Promise<void> => {
    if (!user) throw new Error('User not authenticated');

    const { error } = await supabase
      .from('Teams')
      .delete()
      .eq('id', teamId);

    if (error) throw error;

    // Update local state
    setTeams(prev => {
      const remainingTeams = prev.filter(t => t.id !== teamId);
      // Sort teams alphabetically
      const sortedTeams = [...remainingTeams].sort((a, b) => a.name.localeCompare(b.name));
      
      // Select the first team if available
      const firstTeam = sortedTeams[0] || null;
      if (firstTeam) {
        // Find and sort this team's projects
        const teamProjects = projects
          .filter(p => p.team_id === firstTeam.id)
          .sort((a, b) => a.name.localeCompare(b.name));
        
        // Update localStorage
        if (user) {
          localStorage.setItem(`${user.id}_selectedTeamId`, firstTeam.id);
          if (teamProjects[0]) {
            localStorage.setItem(`${user.id}_selectedProjectId`, teamProjects[0].id);
          } else {
            localStorage.removeItem(`${user.id}_selectedProjectId`);
          }
        }

        // Set the new selections
        handleSetSelectedTeam(firstTeam);
        handleSetSelectedProject(teamProjects[0] || null);
      } else {
        // No teams left
        if (user) {
          localStorage.removeItem(`${user.id}_selectedTeamId`);
          localStorage.removeItem(`${user.id}_selectedProjectId`);
        }
        handleSetSelectedTeam(null);
        handleSetSelectedProject(null);
      }

      return remainingTeams;
    });
  };

  // Fetch teams for the logged-in user
  useEffect(() => {
    const fetchTeams = async () => {
      if (!user) {
        return;
      }

      try {
        setIsLoading(true);
        
        const { data, error } = await supabase
          .from('Teams')
          .select('*')
          .contains('users', [user.id]);

        if (error) {
          throw error;
        }
        
        if (data) {
          setTeams(data);
          
          // If we have a saved team ID, select that team
          const savedTeamId = localStorage.getItem(`${user.id}_selectedTeamId`);
          if (savedTeamId) {
            const savedTeam = data.find(team => team.id === savedTeamId);
            if (savedTeam) {
              setSelectedTeam(savedTeam);
            } else if (data.length > 0) {
              // If saved team not found, select the first team
              setSelectedTeam(data[0]);
            }
          } else if (data.length > 0) {
            // If no saved team, select the first team
            setSelectedTeam(data[0]);
          }
          
          // Trigger invite check after fetching teams
          window.dispatchEvent(new Event('force-check-invites'));
        }
      } catch (error) {
        // Handle error silently
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchTeams();
  }, [user]);

  // Fetch projects for all teams
  useEffect(() => {
    const fetchProjects = async () => {
      if (!user || teams.length === 0) {
        setProjects([]);
        setSelectedProject(null);
        return;
      }

      try {
        setIsLoading(true);
        
        // Get all team IDs
        const teamIds = teams.map(team => team.id);
        
        const { data, error } = await supabase
          .from('Projects')
          .select('*')
          .in('team_id', teamIds);

        if (error) {
          throw error;
        }
        
        if (data) {
          setProjects(data);
          
          // If we have a saved project ID and selected team, select that project
          const savedProjectId = localStorage.getItem(`${user.id}_selectedProjectId`);
          if (savedProjectId && selectedTeam) {
            const savedProject = data.find(
              project => project.id === savedProjectId && project.team_id === selectedTeam.id
            );
            if (savedProject) {
              setSelectedProject(savedProject);
            } else {
              // If saved project not found, select the first project of selected team
              const firstTeamProject = data.find(project => project.team_id === selectedTeam.id);
              setSelectedProject(firstTeamProject || null);
            }
          } else if (selectedTeam) {
            // If no saved project but we have a selected team, select its first project
            const firstTeamProject = data.find(project => project.team_id === selectedTeam.id);
            setSelectedProject(firstTeamProject || null);
          } else {
            setSelectedProject(null);
          }
        }
      } catch (error) {
        // Handle error silently
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchProjects();
  }, [user, teams, selectedTeam]);

  // Log state changes for debugging
  useEffect(() => {
    // Removed debug logging for production
  }, [teams, projects, selectedTeam, selectedProject, isLoading]);

  return (
    <WorkspaceContext.Provider
      value={{
        teams,
        projects,
        selectedTeam,
        selectedProject,
        setSelectedTeam: handleSetSelectedTeam,
        setSelectedProject: handleSetSelectedProject,
        setTeams,
        createProject,
        updateProject,
        deleteProject,
        createTeam,
        updateTeam,
        deleteTeam,
        isLoading
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
}; 